import { useState } from "react"
import { CompanyDetailsView } from "@/components/dashboard/buyer/pages/FindSupplierPage"
import { SubmitRfqPage } from "@/components/dashboard/buyer/pages/SubmitRfqPage"
import type { RfqTarget } from "@/components/dashboard/buyer/pages/SubmitRfqPage"
import { useAuthStore } from "@/store/auth.store"
import { PromptModalCard } from "@/components/shared/PromptModalCard"

interface CompanyDetailPageProps {
  companyId: string
  companyType: "internal" | "external" | "lead"
  onBack: () => void
}

/**
 * Shared company-detail page used by:
 *  - /browse-supplier/companies/$companyId
 *  - /explore/companies/$companyId
 *
 * Encapsulates the RFQ target state and delegates all UI to CompanyDetailsView.
 * Each parent route supplies its own `onBack` handler so the back-navigation
 * destination remains context-aware.
 */
export function CompanyDetailPage({ companyId, companyType, onBack }: CompanyDetailPageProps) {
  const [rfqTarget, setRfqTarget] = useState<RfqTarget | null>(null)
  const { user } = useAuthStore()

  if (!user && (companyType === "lead" || companyType === "external")) {
    return (
      <PromptModalCard
        title="Log in to view supplier details"
        description="Create a free account or log in to view verified lead seller details, contact information, and business credentials."
        onCancel={onBack}
        onCancelText="Keep browsing"
      />
    )
  }

  const handleRequestQuote = (comp: any) => {
    if (companyType === "internal") {
      setRfqTarget({ type: "single-company", company: comp })
    } else if (companyType === "lead") {
      setRfqTarget({ type: "single-lead", seller: comp })
    } else {
      setRfqTarget({
        type: "single-external",
        seller: {
          sellerName: comp.sellerName || comp.name,
          location: comp.companyProfileData?.city
            ? `${comp.companyProfileData.city}, ${comp.companyProfileData.country || ""}`
            : comp.city
            ? `${comp.city}, ${comp.state || ""}`
            : comp.addressLine1 || "",
          rating: comp.rating || undefined,
          reviewsCount: comp.reviewsCount || undefined,
        },
      })
    }
  }

  if (rfqTarget) {
    return (
      <div className="max-w-6xl mx-auto pb-4">
        <SubmitRfqPage
          target={rfqTarget}
          isInline={false}
          onSuccess={() => {
            setRfqTarget(null)
            onBack()
          }}
          onCancel={() => setRfqTarget(null)}
        />
      </div>
    )
  }

  return (
    <CompanyDetailsView
      companyId={companyId}
      type={companyType}
      onBack={onBack}
      onRequestQuote={handleRequestQuote}
    />
  )
}
